import React from 'react';
import {MdClose, MdMenu} from 'react-icons/md';
import {Link as ReactLink, useLocation, useNavigate} from 'react-router-dom';
import NotificationBell from '../../../components/NotificationBell';
import UserOrb from '../../../components/UserOrb';
import assetUrl from '../../../utils/assetUrl';
import {
	Box,
	Button,
	HStack,
	IconButton,
	Image,
	Spacer,
	StackProps,
	VStack,
	useBreakpointValue,
} from 'components/ui/chakra';

interface NavigationMenuProps extends StackProps {}

const NavigationMenu = ({...stackProps}: NavigationMenuProps) => {
	const [isMenuOpen, setIsMenuOpen] = React.useState(false);
	const location = useLocation();
	const navigate = useNavigate();

	const isMobile = useBreakpointValue({
		base: true,
		sm: true,
		md: false,
	});

	const isSelectedProps = (path: string) =>
		location.pathname === path
			? {
					bg: 'linear-gradient(180deg, rgba(248, 225, 216, 0.72) 0%, rgba(255, 253, 249, 0.96) 100%)',
					borderColor: 'clay.300',
					boxShadow:
						'0 10px 18px rgba(180, 138, 120, 0.14), inset 0 1px 0 rgba(255, 255, 255, 0.72)',
					color: 'clay.700',
					fontWeight: '700',
				}
			: {};

	const MenuButtons = () => (
		<>
			<Button variant='navigation' onClick={() => navigate('/')} {...isSelectedProps('/')}>
				Home
			</Button>
			<Button
				variant='navigation'
				onClick={() => navigate('/personal-information')}
				{...isSelectedProps('/personal-information')}
			>
				My Account
			</Button>
			<Button
				variant='navigation'
				onClick={() => navigate('/past-visits')}
				{...isSelectedProps('/past-visits')}
			>
				Past Visits
			</Button>
			<Button
				variant='navigation'
				onClick={() => navigate('/payment-history')}
				{...isSelectedProps('/payment-history')}
			>
				Payments
			</Button>
			<Button
				variant='navigation'
				onClick={() => navigate('/vouchers')}
				{...isSelectedProps('/vouchers')}
			>
				Vouchers
			</Button>
			<Button
				variant='navigation'
				onClick={() => navigate('/support')}
				{...isSelectedProps('/support')}
			>
				Support
			</Button>
			<Button
				variant='navigation'
				onClick={() => navigate('/digital-card')}
				{...isSelectedProps('/digital-card')}
			>
				Digital Card
			</Button>
		</>
	);

	return (
		<>
			<HStack
				maxW='1280px'
				mx='auto'
				px={{base: 4, md: 6}}
				py={4}
				spacing={6}
				{...stackProps}
			>
				{isMobile ? (
					<>
						<HStack gap={3}>
							<IconButton
								aria-label='Menu'
								icon={<MdMenu />}
								variant='ghost'
								onClick={() => setIsMenuOpen(true)}
							/>
							<Box
								as={ReactLink}
								aria-label='Go to patient portal home'
								display='inline-flex'
								to='/'
							>
								<Image
									src={assetUrl('/images/logo.png')}
									alt='Allora Health'
									w='42px'
								/>
							</Box>
						</HStack>
					</>
				) : (
					<>
						<Box
							as={ReactLink}
							aria-label='Go to patient portal home'
							display='inline-flex'
							to='/'
						>
							<Image
								src={assetUrl('/images/logo.png')}
								alt='Allora Health'
								w='44px'
							/>
						</Box>
						<MenuButtons />
					</>
				)}
				<Spacer />
				<NotificationBell />
				<UserOrb
					pos='relative'
					left={undefined}
					bottom={undefined}
					top={undefined}
					zIndex={undefined}
					avatarMargin={0}
				/>
			</HStack>

			{isMenuOpen && (
				<Box bg='rgba(43, 36, 31, 0.32)' inset={0} position='fixed' zIndex='sticky'>
					<Box
						background='rgba(255, 253, 249, 0.96)'
						borderRight='1px solid'
						borderColor='border'
						boxShadow='0 24px 45px rgba(79, 74, 68, 0.14)'
						h='100%'
						maxW='22rem'
						p={5}
					>
						<VStack alignItems='flex-start' gap={4}>
							<HStack justifyContent='space-between' w='full'>
								<Box
									as={ReactLink}
									aria-label='Go to patient portal home'
									display='inline-flex'
									to='/'
									onClick={() => setIsMenuOpen(false)}
								>
									<Image
										src={assetUrl('/images/logo.png')}
										alt='Allora Health'
										w='42px'
									/>
								</Box>
								<IconButton
									aria-label='Close'
									icon={<MdClose />}
									variant='ghost'
									onClick={() => setIsMenuOpen(false)}
								/>
							</HStack>
							<MenuButtons />
						</VStack>
					</Box>
				</Box>
			)}
		</>
	);
};

export default NavigationMenu;
